import React, { useState } from 'react';
import { 
  FolderOpen, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  User,
  CheckSquare,
  XSquare,
  TrendingUp
} from 'lucide-react';

import { enqueteurDashboardData } from '../../data/mockData';
import DossierCard from '../../components/Enqueteur/DossierCard';

const EnqueteurDashboard = () => {
  const [dossiersEnAttente, setDossiersEnAttente] = useState(enqueteurDashboardData.dossiersEnAttente);
  const [dossiersEnCours, setDossiersEnCours] = useState(enqueteurDashboardData.dossiersEnCours);
  const dossiersTermines = enqueteurDashboardData.dossiersTermines;
  
  // États pour les modals de confirmation
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [selectedDossierId, setSelectedDossierId] = useState(null);

  const openAcceptModal = (dossierId) => {
    setSelectedDossierId(dossierId);
    setShowAcceptModal(true);
  };

  const openRefuseModal = (dossierId) => {
    setSelectedDossierId(dossierId);
    setShowRefuseModal(true);
  };

  const handleAcceptDossier = () => {
    // Trouver le dossier
    const dossier = dossiersEnAttente.find(d => d.id === selectedDossierId);
    if (dossier) {
      // Le retirer des dossiers en attente
      setDossiersEnAttente(dossiersEnAttente.filter(d => d.id !== selectedDossierId));
      // L'ajouter aux dossiers en cours avec le statut mis à jour
      const dossierAccepte = {
        ...dossier,
        statut: 'en_cours',
        dateAcceptation: new Date().toISOString()
      };
      setDossiersEnCours([dossierAccepte, ...dossiersEnCours]);
      console.log(`Dossier ${dossier.reference} accepté`);
    }
    // Fermer le modal
    setShowAcceptModal(false);
    setSelectedDossierId(null);
  };

  const handleRefuseDossier = () => {
    // Trouver le dossier
    const dossier = dossiersEnAttente.find(d => d.id === selectedDossierId);
    if (dossier) {
      // Le retirer des dossiers en attente
      setDossiersEnAttente(dossiersEnAttente.filter(d => d.id !== selectedDossierId));
      console.log(`Dossier ${dossier.reference} refusé`);
    }
    // Fermer le modal
    setShowRefuseModal(false);
    setSelectedDossierId(null);
  };

  const dossiersCounts = {
    total: dossiersEnAttente.length + dossiersEnCours.length + dossiersTermines.length,
    enAttente: dossiersEnAttente.length,
    enCours: dossiersEnCours.length,
    termines: dossiersTermines.length
  };

  return (
    <div className="animate-slide-in">
      {/* Modal de confirmation d'acceptation */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-port-gray-900 mb-4">
              Confirmation d'acceptation
            </h3>
            <p className="text-port-gray-700 mb-6">
              Veuillez confirmer l'acceptation du dossier, un mail va être envoyé à Provelliance pour les informer.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAcceptModal(false);
                  setSelectedDossierId(null);
                }}
                className="flex-1 px-4 py-2 border border-port-gray-300 text-port-gray-700 rounded-lg hover:bg-port-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleAcceptDossier}
                className="flex-1 px-4 py-2 bg-atl-green-600 text-white rounded-lg hover:bg-atl-green-700 transition-colors font-medium"
              >
                Confirmer l'acceptation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de refus */}
      {showRefuseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-port-gray-900 mb-4">
              Confirmation de refus
            </h3>
            <p className="text-port-gray-700 mb-6">
              Veuillez confirmer le refus du dossier, Provelliance l'attribuera à un autre enquêteur.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRefuseModal(false);
                  setSelectedDossierId(null);
                }}
                className="flex-1 px-4 py-2 border border-port-gray-300 text-port-gray-700 rounded-lg hover:bg-port-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleRefuseDossier}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Mobile-First */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-port-gray-900">
              Tableau de Bord
            </h1>
          </div>
        </div>

        {/* Indicateurs / Stats rapides */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg p-3 shadow-sm border border-port-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-atl-blue-100 rounded-lg">
                <FolderOpen className="h-4 w-4 text-atl-blue-600" />
              </div>
              <div className="ml-2">
                <p className="text-lg font-bold text-port-gray-900">{dossiersCounts.total}</p>
                <p className="text-xs text-port-gray-600">Total</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-sm border border-port-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="h-4 w-4 text-orange-600" />
              </div>
              <div className="ml-2">
                <p className="text-lg font-bold text-orange-600">{dossiersCounts.enAttente}</p>
                <p className="text-xs text-port-gray-600">En attente</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-sm border border-port-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="ml-2">
                <p className="text-lg font-bold text-yellow-600">{dossiersCounts.enCours}</p>
                <p className="text-xs text-port-gray-600">En cours</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-sm border border-port-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-atl-green-100 rounded-lg">
                <CheckCircle className="h-4 w-4 text-atl-green-600" />
              </div>
              <div className="ml-2">
                <p className="text-lg font-bold text-atl-green-600">{dossiersCounts.termines}</p>
                <p className="text-xs text-port-gray-600">Terminés</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section : Dossiers en attente d'acceptation */}
      {dossiersEnAttente.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-5 w-5 text-orange-600 mr-2" />
            <h2 className="text-lg font-semibold text-port-gray-900">
              Dossiers en attente d'acceptation
            </h2>
            <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              {dossiersEnAttente.length}
            </span>
          </div>

          <div className="space-y-3">
            {dossiersEnAttente.map(dossier => (
              <div key={dossier.id} className="bg-white rounded-lg shadow-sm border-2 border-orange-200">
                <div className="p-4">
                  {/* Informations du dossier */}
                  <div className="mb-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-medium text-atl-blue-600 text-base">
                          {dossier.reference}
                        </h3>
                        <p className="text-sm text-port-gray-500">
                          {dossier.client}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-port-gray-900">
                        <User className="h-4 w-4 text-port-gray-400 mr-2" />
                        <span>{dossier.assure.prenom} {dossier.assure.nom}</span>
                      </div>
                      
                      <div className="text-sm text-port-gray-700">
                        <span className="font-medium">{dossier.typeSinistre}</span> - {dossier.natureSinistre}
                      </div>

                      <div className="text-sm text-port-gray-600">
                        📍 {dossier.lieu}
                      </div>

                      <div className="text-sm text-port-gray-600">
                        {dossier.description}
                      </div>

                      <div className="text-sm font-medium text-atl-blue-600">
                        Montant estimé : {dossier.montantEstime.toLocaleString('fr-FR')} €
                      </div>
                    </div>
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex space-x-3 pt-3 border-t border-port-gray-200">
                    <button
                      onClick={() => openAcceptModal(dossier.id)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-atl-green-600 text-white rounded-lg hover:bg-atl-green-700 transition-colors font-medium"
                    >
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Accepter le dossier
                    </button>
                    <button
                      onClick={() => openRefuseModal(dossier.id)}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      <XSquare className="h-4 w-4 mr-2" />
                      Refuser
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section : Dossiers en cours */}
      {dossiersEnCours.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="h-5 w-5 text-yellow-600 mr-2" />
            <h2 className="text-lg font-semibold text-port-gray-900">
              Dossiers en cours
            </h2>
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
              {dossiersEnCours.length}
            </span>
          </div>

          <div className="space-y-3">
            {dossiersEnCours.map(dossier => (
              <DossierCard key={dossier.id} dossier={dossier} />
            ))}
          </div>
        </div>
      )}

      {/* État vide */}
      {dossiersEnAttente.length === 0 && dossiersEnCours.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-port-gray-200">
          <FolderOpen className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-port-gray-900 mb-2">
            Aucun dossier actif
          </h3>
          <p className="text-port-gray-500">
            Vous n'avez aucun dossier en attente ou en cours pour le moment.
          </p>
        </div>
      )}
    </div>
  );
};

export default EnqueteurDashboard;
